import { VariationIdNameOrder } from "@intelligems/ig-types";

import {
  UpsellProductRow,
  VariationProductMetric,
} from "@src/features/analytics/types";

export type UpsellProductSource = "checkout_upsell" | "onsite_upsell";

// The fields of a variation-products row this mapper reads, for either source.
// The per-source breakdown columns are optional: Tinybird only projects them
// when the matching includeXUpsellBreakdown flag is set.
type UpsellProductFields = Pick<
  VariationProductMetric,
  "variation_id" | "product_title"
> &
  Partial<
    Pick<
      VariationProductMetric,
      | "unit_quantity_from_checkout_upsell"
      | "net_product_revenue_from_checkout_upsell"
      | "gross_product_profit_from_checkout_upsell"
      | "unit_quantity_from_onsite_upsell"
      | "net_product_revenue_from_onsite_upsell"
      | "gross_product_profit_from_onsite_upsell"
    >
  >;

const num = (metric: UpsellProductFields, key: keyof UpsellProductFields) =>
  (metric[key] as number | undefined) ?? 0;

/**
 * Map raw `variation_products` metric rows into per-product upsell rows for the
 * given upsell source, dropping rows with no upsell activity so the table shows
 * only real offers.
 */
export const toUpsellProductRows = (
  metrics: UpsellProductFields[] | undefined,
  variations: VariationIdNameOrder[],
  source: UpsellProductSource
): UpsellProductRow[] => {
  if (!metrics?.length) return [];

  return metrics
    .map((metric) => ({
      offer: metric.product_title,
      testGroup:
        variations.find((variation) => variation.id === metric.variation_id)
          ?.name ?? metric.variation_id,
      variationId: metric.variation_id,
      unitsSold: num(metric, `unit_quantity_from_${source}`),
      upsellRevenue: num(metric, `net_product_revenue_from_${source}`),
      upsellProfit: num(metric, `gross_product_profit_from_${source}`),
    }))
    .filter((row) => row.unitsSold > 0 || row.upsellRevenue > 0);
};
